import React, { useEffect } from 'react'
import cn from 'classnames'
import styles from './floating-filter-icon.module.scss'
import FilterIcon_ from 'components/filters/FilterIcon'
const FilterIcon = FilterIcon_ as any
import PublicIcon from 'components/shared/icons/PublicIcon'
import MusicIcon from 'components/shared/icons/MusicIcon'
import FollowingIcon from 'components/shared/icons/FollowingIcon'
import UserIcon from 'components/users/UserIcon'
import useStore from 'state/knovStore'
import { ensureSpaceUsersLoaded } from 'state/spaceUsers'
import { User } from 'types/users'
import { PanelState } from 'state/PanelState'
import { getCachedQuest } from 'state/cache'
import { Filter } from 'components/filters/useStreamFilters'
import { streamOrderConfigs, getFilterMapping } from './StreamOrder'
import { isTopOrder } from './streamOrderMapping'
import useTeamLookup from 'components/teams/useTeamLookup'

interface FloatingFilterIconProps {
    panel: PanelState
}

// Function to get the appropriate icon class based on filter and mapping
const getStreamOrderIconClass = (filter: Filter | undefined | null): string | undefined => {
    if (filter?.history || filter?.notifications || filter?.starred) return

    const filterMapping = getFilterMapping(filter)
    const defaultOrderKey = Object.keys(filterMapping)[0]
    const currentOrderKey = filter?.order || defaultOrderKey

    // Special case for relevance based on query
    if (filter?.query) {
        return streamOrderConfigs.relevance.icon
    }

    // Find the config for the current order key. The Top dropdown makes every
    // Top flavor (top_new / upvote / upvalue) reachable while the mapping keys
    // that slot under a single order, so fall back to the shared Top icon.
    const currentOrderConfig = filterMapping[currentOrderKey]

    if (!currentOrderConfig && isTopOrder(currentOrderKey)) {
        return streamOrderConfigs.upvalue.icon
    }

    return currentOrderConfig?.icon
}

// The stream's scope icon (public, following, private, a channel, a user, the
// notifications bell, ...): the capsule's icon, and with fixed_header the
// header's, so a stream reads the same in both places.
export function PanelScopeIcon({
    panel,
    filter,
    single = true,
    music = false,
}: {
    panel: PanelState
    filter: Filter | undefined | null
    single?: boolean
    // A music thread's channel: the public orange circle, with a note.
    music?: boolean
}) {
    const hasTeamFilter = filter?.team_ids?.length === 1
    const streamTeamId = hasTeamFilter ? filter.team_ids[0] : null
    const { team: currentTeam } = useTeamLookup(streamTeamId)

    const showPublicIcon = filter?.public && !filter?.following
    const showFollowingIcon = filter?.following

    // Special-case: when viewing "user ordinals" (filter.user_ordinals),
    // the user icon should dominate and we should not show the ordinals icon.
    const filterUserId = filter?.user_ordinals || filter?.user
    const filterUser = useStore(state =>
        state.users?.find((u: User) => String(u?.id || '') === String(filterUserId || '')),
    )
    useEffect(() => {
        if (filterUserId && !filterUser) ensureSpaceUsersLoaded()
    }, [filterUserId, filterUser])
    const iconUser = filterUserId ? filterUser || { id: filterUserId } : gon?.currentUser
    const showUserDominantIcon = !!filter?.user_ordinals && !showPublicIcon && !showFollowingIcon

    if (music) {
        return (
            <MusicIcon
                contextStyles={cn(
                    styles.filterIcon,
                    styles.publicIcon,
                    single && styles.filterIconSingle,
                )}
            />
        )
    }
    if (showPublicIcon) {
        return (
            <PublicIcon
                contextStyles={cn(
                    styles.filterIcon,
                    styles.publicIcon,
                    single && styles.filterIconSingle,
                )}
            />
        )
    }
    if (showFollowingIcon) {
        return (
            <FollowingIcon
                contextStyles={cn(
                    styles.filterIcon,
                    styles.followingIcon,
                    single && styles.filterIconSingle,
                )}
            />
        )
    }
    if (showUserDominantIcon) {
        return (
            <UserIcon
                user={filterUser || { id: filterUserId }}
                contextStyles={cn(styles.filterIcon, single && styles.filterIconSingle)}
                size="medium"
            />
        )
    }
    return (
        <FilterIcon
            disable={true}
            filter={filter}
            team={currentTeam}
            panel={panel}
            user={iconUser}
            contextStyles={cn(styles.filterIcon, single && styles.filterIconSingle)}
            fontIcon={styles.fontIcon}
            privateIcon={styles.privateIcon}
            notiIcon={styles.notiIcon}
            publicIcon={styles.publicIcon}
            allIcon={styles.allIcon}
            historyIcon={styles.historyIcon}
            hodlockerIcon={styles.hodlockerIcon}
            twetchIcon={styles.twetchIcon}
            type="control"
        />
    )
}

export default function FloatingFilterIcon({ panel }: FloatingFilterIconProps) {
    const quest = panel.filter?.questId ? getCachedQuest(panel.filter.questId) : null
    const parentUser = quest?.parent?.user
    const streamOrderIconClass = getStreamOrderIconClass(panel.filter)

    return (
        <div className={cn(styles.floatingFilterComp, parentUser && styles.thread)}>
            {!parentUser && streamOrderIconClass && (
                <div className={styles.streamOrderIconContainer}>
                    <i className={cn(streamOrderIconClass, styles.streamOrderIcon)} />
                </div>
            )}

            <PanelScopeIcon panel={panel} filter={panel.filter} single={!parentUser} />

            {parentUser && (
                <UserIcon user={parentUser} contextStyles={styles.userIcon} size="medium" />
            )}

            {/* Threads always get the chevron because the capsule is their
                jump-to-root control. Feed and notification new items use the
                sibling NewItemsPill, which carries its own chevron. */}
            {parentUser && (
                <i className={cn('fa fa-chevron-up', styles.jumpChevron)} aria-hidden="true" />
            )}
        </div>
    )
}
